/**
 * Mounted once per window (app shell; member inbox): the incoming-call banner
 * and the panel of the call this window is in (TRUS-668).
 *
 * The panel docks inside the messaging pane when <MessagingWorkspace> is on
 * screen (it registers a dock element), and floats over the bottom-right of the
 * window otherwise — so a call survives navigating to another page. It is never
 * portalled into the dock: moving it between containers would remount the
 * panel, and a remount leaves (and possibly ends) the call. It stays put in the
 * tree and is positioned over the dock's bottom-right corner instead.
 *
 * The docked panel can be dragged by its header (useDraggablePanel): only the
 * wrapper's left / top change, so it still never remounts.
 *
 * v3: a `?call=ID` link (staff /workspace-messaging?call=ID, member
 * /member/inbox?call=ID — what an invite links to) opens the join flow here.
 * An invitee who cannot read the channel gets the panel alone.
 */

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { isSecondaryAppWindow } from '@/lib/desktopWindow';
import { isInAnyPopout } from '@/lib/popout';
import type { Call } from '@/services/callsApi';
import { callStore, OPEN_CALL_CHANNEL_EVENT, useCallStore } from './callStore';
import { useCallsApi, useCallsConfig, useIncomingCalls } from './useCalls';
import { IncomingCallBanner } from './IncomingCallBanner';
import { CallPanelPositionContext, useDraggablePanel } from './useDraggablePanel';

// twilio-video is loaded only once a call opens.
const CallPanel = lazy(() => import('./CallPanel').then((m) => ({ default: m.CallPanel })));

const GAP = 16;
// Inside the messaging pane, sit above the composer row (send button).
const DOCK_BOTTOM_CLEARANCE = 88;

/** Offsets that put a fixed element at the dock's bottom-right corner. */
function useDockOffsets(dock: HTMLElement | null): { right: number; bottom: number } {
  const [offsets, setOffsets] = useState({ right: GAP, bottom: GAP });
  useEffect(() => {
    if (!dock) {
      setOffsets({ right: GAP, bottom: GAP });
      return;
    }
    const update = () => {
      const r = dock.getBoundingClientRect();
      setOffsets({
        right: Math.max(GAP, window.innerWidth - r.right + GAP),
        bottom: Math.max(GAP, window.innerHeight - r.bottom + DOCK_BOTTOM_CLEARANCE),
      });
    };
    update();
    window.addEventListener('resize', update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(dock);
    return () => {
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [dock]);
  return offsets;
}

export { OPEN_CALL_CHANNEL_EVENT };

/** Fired by the web-push click handler (lib/pushNavigation.ts) with `{ callId, channelId }`. */
export const PUSH_OPEN_CALL_EVENT = 'weavein:push:open-call';

/** The call a `?call=ID` link points at, or null. */
export function callIdFromSearch(search: string): number | null {
  try {
    const raw = new URLSearchParams(search).get('call');
    const id = Number(raw);
    return raw && Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/** Open a call as the join flow: consent first when it records; the conversation only if readable. */
export function openCallFromServer(call: Call, channelName?: string) {
  const standalone = call.can_open_channel === false;
  callStore.open({
    callId: call.id,
    channelId: call.channel_id,
    mode: call.mode,
    channelName,
    consentNeeded: !!call.recording && !call.my_recording_consent,
    standalone,
  });
  if (!standalone) callStore.requestChannel(call.channel_id);
}

/** `?call=ID` in this window's URL → the join flow, once. Not in pop-outs (they carry their own call). */
function useCallDeepLink(enabled: boolean) {
  const openCallById = useOpenCallById();
  const handled = useRef(false);
  useEffect(() => {
    if (!enabled || handled.current || isInAnyPopout()) return;
    const callId = callIdFromSearch(window.location.search);
    if (callId == null) return;
    handled.current = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('call');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      /* ignore */
    }
    void openCallById(callId);
  }, [enabled, openCallById]);

  // A call notification clicked while the app is already open routes in place
  // (no reload, so no ?call= in the URL) and announces the call instead.
  useEffect(() => {
    if (!enabled || isInAnyPopout()) return;
    const onPushOpen = (e: Event) => {
      const callId = Number((e as CustomEvent<{ callId?: unknown }>).detail?.callId);
      if (Number.isInteger(callId) && callId > 0) void openCallById(callId);
    };
    window.addEventListener(PUSH_OPEN_CALL_EVENT, onPushOpen);
    return () => window.removeEventListener(PUSH_OPEN_CALL_EVENT, onPushOpen);
  }, [enabled, openCallById]);
}

/** Fetch a call by id and open its join flow, with a toast when it's gone or ended. */
function useOpenCallById() {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  return useCallback(
    async (callId: number) => {
      let call: Call;
      try {
        call = await api.get(callId);
      } catch {
        toast.error(t('messaging.calls.linkUnavailable', "This call isn't available to you, or it no longer exists."));
        return;
      }
      if (call.status !== 'active') {
        toast(t('messaging.calls.callEnded', 'This call has ended.'));
        return;
      }
      if (call.is_participant) return;
      openCallFromServer(call);
    },
    [api, t],
  );
}

interface CallsHostProps {
  /** Poll the ringing list + ring. Default: the main window only (not pop-outs / tab frames). */
  ring?: boolean;
}

export function CallsHost({ ring }: CallsHostProps) {
  const { config } = useCallsConfig();
  const { session, dock } = useCallStore();
  const shouldRing = ring ?? !isSecondaryAppWindow();
  const incoming = useIncomingCalls(config.enabled && shouldRing);
  const offsets = useDockOffsets(dock);
  useCallDeepLink(config.enabled);
  const docked = !!session && !session.fullWindow;
  const drag = useDraggablePanel(docked);
  const position = useMemo(() => ({ moved: drag.moved, reset: drag.reset }), [drag.moved, drag.reset]);

  const onJoined = (call: Call) => {
    // v3: an invitee who cannot read the channel stays where they are.
    if (call.can_open_channel === false) return;
    callStore.requestChannel(call.channel_id);
  };

  let panel: React.ReactNode = null;
  if (session) {
    const fullWindow = !!session.fullWindow;
    const node = (
      <Suspense fallback={null}>
        <CallPanel key={session.callId} request={session} variant={fullWindow ? 'window' : 'docked'} />
      </Suspense>
    );
    panel = fullWindow ? (
      <div className="fixed inset-0 z-[80] bg-slate-900">{node}</div>
    ) : (
      <div
        ref={drag.ref}
        className="pointer-events-none fixed z-[60]"
        // Moved by the user: left / top (same element — the panel never remounts).
        style={drag.pos ? { left: drag.pos.x, top: drag.pos.y } : offsets}
        data-docked={dock ? 'pane' : 'window'}
        data-moved={drag.moved ? 'true' : undefined}
        data-testid="call-panel-frame"
        {...drag.handlers}
      >
        <CallPanelPositionContext.Provider value={position}>{node}</CallPanelPositionContext.Provider>
      </div>
    );
  }

  return (
    <>
      {config.enabled && <IncomingCallBanner calls={incoming} ring={shouldRing} onJoined={onJoined} />}
      {panel}
    </>
  );
}

export default CallsHost;
